import React, { useEffect, useState, useCallback } from 'react';
import useOrders from '../../hooks/useOrders';
import useDriverAssignmentApi from '../../hooks/useDriverAssignmentApi';

import { getAllDrivers } from '../../api/driverApi';
import { toast } from 'react-toastify';
import useApi from '../../hooks/useApi';
import '../../styles/DriverAssignment.css';

const AssignOrder = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersState, setOrdersState] = useState([]);
  const { orders, pagination, isLoading, error } = useOrders({ 
    role: 'admin',
    page: currentPage,
    perPage: 10,

  });
  const {
    getNearbyDrivers,
    autoAssignDrivers,
    manualAssignDrivers,
    getAssignmentStatus,
    loading: assignmentLoading
  } = useDriverAssignmentApi();

  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState({});
  const [assigningOrderId, setAssigningOrderId] = useState(null);
  const [nearbyDriversData, setNearbyDriversData] = useState({});
  const [selectedDriversForOrder, setSelectedDriversForOrder] = useState({});
  const [loadingNearbyDrivers, setLoadingNearbyDrivers] = useState({});
  const [showPending, setShowPending] = useState(true);
  const [assignedOrders, setAssignedOrders] = useState(new Set());
  const [pendingAssignments, setPendingAssignments] = useState({});
  const [modalOrderId, setModalOrderId] = useState(null);
  const [activeTab, setActiveTab] = useState('available'); // 'available' or 'pending'
  const [isSendingDrivers, setIsSendingDrivers] = useState(false);

  // Check if order is already assigned
  const isOrderAssigned = useCallback((order) => {
    return assignedOrders.has(order.order_id) ||
           order.delivery_assignment ||
           order.status === 'ongoing' ||
           order.status === 'completed';
  }, [assignedOrders]);



  // Real-time monitoring for pending assignments
  const startRealTimeMonitoring = useCallback((orderId) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await getAssignmentStatus(orderId);
        
        if (status?.is_assigned || status?.data?.is_assigned) {
          clearInterval(pollInterval);
          setPendingAssignments(prev => {
            const { [orderId]: removed, ...rest } = prev;
            return rest;
          });
          setAssignedOrders(prev => new Set([...prev, parseInt(orderId)]));
          toast.success('🎉 Driver confirmed! Order is now assigned and on its way.', {
            autoClose: 5000
          });
        }
      } catch (error) {
        console.error('Polling error for order', orderId, error);
      }
    }, 10000);

    setTimeout(() => {
      clearInterval(pollInterval);
    }, 15 * 60 * 1000);
  }, [getAssignmentStatus]);

  // useEffect(() => {
  //   getAllDrivers().then(setDrivers);
  // }, []);

  // Sync ordersState with orders from hook
useEffect(() => {
  if (!isLoading && orders.length > 0) {
    setOrdersState(prev => {
      const newOrders = orders.filter(
        o => !prev.some(p => p.order_id === o.order_id)
      );
      return [...prev, ...newOrders];
    });
  }
}, [orders]);



  const { post } = useApi();
  const assignDriverMutation = post('/admin/assign-driver', {
    onSuccess: (data, variables) => {
      if (data.status === 'success') {
        toast.success('Driver assigned successfully!');
        setOrdersState(prev => prev.filter(order => order.order_id !== variables.order_id));
      }
      else toast.error(data.message || 'Failed to assign driver.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to assign driver.');
    }
  });



  // Load nearby drivers for an order
  const loadNearbyDrivers = async (orderId) => {
    setLoadingNearbyDrivers(prev => ({ ...prev, [orderId]: true }));
    try {
      const data = await getNearbyDrivers(orderId);
      setNearbyDriversData(prev => ({ ...prev, [orderId]: data }));

      if (!data.data?.drivers || data.data.drivers.length === 0) {
        toast.warning('No drivers found within the specified distance. Try increasing the search radius.');
      } else {
        toast.success(`Found ${data.data.drivers.length} nearby drivers`);
      }
    } catch (error) {
      console.error('Error loading nearby drivers:', error);
      if (error?.response?.status === 422) {
        toast.error('Invalid order ID or parameters');
      } else if (error?.response?.status === 403) {
        toast.error('Unauthorized access');
      } else if (error?.response?.status === 400) {
        const message = error?.response?.data?.message || 'Customer location not available';
        toast.error(message);
      } else {
        toast.error('Failed to load nearby drivers. Please check your connection and try again.');
      }
    } finally {
      setLoadingNearbyDrivers(prev => ({ ...prev, [orderId]: false }));
    }
  };

  // Handle checkbox selection for manual assignment
  const handleDriverCheckboxChange = (orderId, driverId, checked) => {
    setSelectedDriversForOrder(prev => {
      const orderDrivers = prev[orderId] || [];
      if (checked) {
        return { ...prev, [orderId]: [...orderDrivers, driverId] };
      } else {
        return { ...prev, [orderId]: orderDrivers.filter(id => id !== driverId) };
      }
    });
  };

  // Handle automatic assignment (for manual button clicks)
  const handleAutoAssign = useCallback(async (orderId) => {
    if (assigningOrderId === orderId || isOrderAssigned(ordersState.find(o => o.order_id === orderId))) {
      return;
    }

    setAssigningOrderId(orderId);
    try {
      const result = await autoAssignDrivers(orderId);
      console.log('Auto assign result (manual):', result);
      const requestsSent = result?.requests_sent || result?.data?.requests_sent || 0;
      
      if (requestsSent > 0) {
        toast.success(`Assignment requests sent to ${requestsSent} driver${requestsSent !== 1 ? 's' : ''}`);
         // ✅ START REAL-TIME MONITORING HERE
        startRealTimeMonitoring(orderId);
        setPendingAssignments(prev => ({
          ...prev,
          [orderId]: {
            type: 'auto',
            requestsSent: requestsSent,
            sentAt: new Date().toISOString(),
            status: 'waiting_confirmation'
          }
        }));
      } else {
        toast.warning('No drivers available in the area');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to auto-assign drivers';
      toast.error(errorMessage);
      console.error('Auto-assign error:', error);
    } 
    finally {
      setAssigningOrderId(null);
    }
  }, [assigningOrderId, ordersState, autoAssignDrivers, isOrderAssigned]);

  // Handle manual assignment
const handleManualAssign = async (orderId) => {
  const selectedDriverIds = selectedDriversForOrder[orderId] || [];
  if (selectedDriverIds.length === 0) {
    toast.error('Please select at least one driver');
    return;
  }

  // START LOADING
  setIsSendingDrivers(true);
  setAssigningOrderId(orderId);

  try {
    const result = await manualAssignDrivers(orderId, selectedDriverIds);
    console.log('Manual assign result:', result);

    const requestsSent =
      result?.requests_sent ||
      result?.data?.requests_sent ||
      selectedDriverIds.length;

    toast.success(
      `Assignment requests sent to ${requestsSent} driver${
        requestsSent !== 1 ? 's' : ''
      }`
    );
    startRealTimeMonitoring(orderId);

    setPendingAssignments((prev) => ({
      ...prev,
      [orderId]: {
        type: 'manual',
        requestsSent: requestsSent,
        selectedDrivers: selectedDriverIds,
        sentAt: new Date().toISOString(),
        status: 'waiting_confirmation',
      },
    }));

    // OPTIONAL: Close modal after success
    closeDriverSelectionModal();

  } catch (error) {
    toast.error(
      error?.response?.data?.message || 'Failed to manually assign drivers'
    );
  } finally {
    // STOP LOADING
    setIsSendingDrivers(false);
    setAssigningOrderId(null);
  }
};




  // Filter orders into different categories
  const paidOrders = ordersState.filter(order =>
    order.status === 'pending' && 
    !isOrderAssigned(order) && 
    !pendingAssignments[order.order_id]
  );

  const pendingOrders = ordersState.filter(order =>
    order.status === 'pending' && 
    pendingAssignments[order.order_id] && 
    !isOrderAssigned(order)
  );

const fetchPendingAssignments = useCallback(async () => {
  const pending = {};

  for (const order of ordersState) {
    if (order.status !== 'pending') continue;

    try {
      const res = await getAssignmentStatus(order.order_id);
      const pendingRequests = res?.data?.assignment_requests?.filter(
        (req) => req.status === 'pending'
      ) || [];

      if (pendingRequests.length > 0) {
        // Determine assignment type - check for both "automatic" and "auto"
        let assignmentType = 'manual';
        const requestsSent = pendingRequests.length;
        
        // Check if ANY request is manual
        const hasManualAssignment = pendingRequests.some(req => 
          req.assignment_type === 'manual' || 
          req.assignment_type === 'manual_assignment'
        );
        
        // Check if ANY request is automatic/auto
        const hasAutoAssignment = pendingRequests.some(req => 
          req.assignment_type === 'automatic' || 
          req.assignment_type === 'auto' ||
          req.assignment_type === 'auto_assignment'
        );

        // Logic: If it has auto assignment and no manual, it's auto
        if (hasAutoAssignment && !hasManualAssignment) {
          assignmentType = 'auto';
        } else if (hasManualAssignment) {
          assignmentType = 'manual';
        }

        // Find the earliest sent time
        const sentAt = pendingRequests.reduce((earliest, req) => {
          const requestTime = new Date(req.sent_at || req.created_at);
          const earliestTime = new Date(earliest);
          return requestTime < earliestTime ? req.sent_at : earliest;
        }, pendingRequests[0]?.sent_at);

        pending[order.order_id] = {
          type: assignmentType,
          requestsSent: requestsSent,
          sentAt: sentAt || new Date().toISOString(),
          status: 'waiting_confirmation',
          requests: pendingRequests
        };
      }
    } catch (err) {
      console.error(`Failed to fetch assignment status for order ${order.order_id}`, err);
    }
  }

  setPendingAssignments(pending);
}, [ordersState, getAssignmentStatus]);


  useEffect(() => {
    if (ordersState.length > 0) {
      fetchPendingAssignments();
    }
  }, [ordersState]);
  
useEffect(() => {
  if (ordersState.length > 0) {
    const today = new Date();
    const todayAssigned = new Set(
      ordersState
        .filter(order => order.assigned_at)
        .filter(order => {
          const assignedDate = new Date(order.assigned_at);
          return assignedDate.toDateString() === today.toDateString();
        })
        .map(order => order.order_id)
    );
    setAssignedOrders(todayAssigned);
  }
}, [ordersState]);

  const openDriverSelectionModal = (orderId) => {
    setModalOrderId(orderId);
    loadNearbyDrivers(orderId);
  };

  const closeDriverSelectionModal = () => {
    setModalOrderId(null);
    setSelectedDriversForOrder(prev => ({ ...prev, [modalOrderId]: [] }));
  };




  if (isLoading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading orders...</p>
    </div>
  );
  
  if (error) return (
    <div className="error-container">
      <div className="error-icon">⚠️</div>
      <h3>Error loading orders</h3>
      <p>{error.message || 'Unknown error'}</p>
      <button className="btn btn-primary" onClick={() => window.location.reload()}>
        Try Again
      </button>
    </div>
  );

  return (
    <div className="assign-order-container">
      <div className="assign-order-header">
        <div className="header-content">
          <div className="header-title">
            <div className="title-icon">
              <i className="ri-truck-line"></i>
            </div>
            <div>
              <h1>Driver Assignment</h1>
              <p>Assign drivers to pending orders efficiently</p>
            </div>
          </div>
          <div className="header-stats">
            <div className="stat-card">
              <div className="stat-value">{paidOrders.length}</div>
              <div className="stat-label">Available Orders</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{pendingOrders.length}</div>
              <div className="stat-label">Waiting Confirmation</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{assignedOrders.size}</div>
              <div className="stat-label">Assigned Today</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'available' ? 'active' : ''}`}
            onClick={() => setActiveTab('available')}
          >
            <i className="ri-list-check-2"></i>
            Available Orders ({paidOrders.length})
          </button>
          <button 
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <i className="ri-time-line"></i>
            Waiting Confirmation ({pendingOrders.length})
          </button>
        </div>
      </div>

      {/* Pending Orders Tab */}
      {activeTab === 'pending' && pendingOrders.length > 0 && (
        <div className="orders-section">
          <div className="orders-grid">
            {pendingOrders.map((order) => {
              const assignmentInfo = pendingAssignments[order.order_id];
              return (
                <div key={order.order_id} className="order-card pending">
                  <div className="order-header">
                    <div className="order-badge pending">
                      <i className="ri-time-line"></i>
                      Waiting Response
                    </div>
                    <div className="order-id">Order #{order.order_id}</div>
                  </div>
                  
                  <div className="order-content">
                    <div className="customer-info">
                      <div className="customer-avatar">
                        <i className="ri-user-3-fill"></i>
                      </div>
                      <div className="customer-details">
                        <h4>{order.customer?.user?.name || 'Guest Customer'}</h4>
                        <p>{order.customer?.user?.phone_no || 'No phone'}</p>
                      </div>
                    </div>
                    
                    <div className="order-meta">
                      <div className="meta-item">
                        <i className="ri-calendar-2-line"></i>
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                      <div className="meta-item">
                        <i className="ri-money-dollar-circle-line"></i>
                       {(order.order_items?.[0]?.currency_symbol || '')}{' '}{order.total_amount || 'N/A'}
                      </div>
                      <div className="meta-item">
                        <i className="ri-box-3-line"></i>
                        {order.order_items?.length || 0} items
                      </div>
                    </div>

                    <div className="assignment-info">
                      <div className="assignment-type">
                        <i className="ri-send-plane-line"></i>
                        {assignmentInfo?.type === 'auto' ? 'Auto Assignment' : 'Manual Assignment'}
                      </div>
                      <div className="assignment-details">
                        Sent to {assignmentInfo?.requestsSent || 0} drivers • {new Date(assignmentInfo?.sentAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Orders Tab */}
      {activeTab === 'available' && (
        <div className="orders-section">
          {paidOrders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="ri-checkbox-circle-line"></i>
              </div>
              <h3>All Orders Assigned!</h3>
              <p>No pending orders require driver assignment at this time.</p>
            </div>
          ) : (
            <>
              <div className="orders-grid">
                {paidOrders.map((order) => (
                  <div key={order.order_id} className="order-card available">
                    <div className="order-header">
                      <div className="order-badge available">
                        <i className="ri-check-line"></i>
                        Ready to Assign
                      </div>
                      <div className="order-id">Order #{order.order_id}</div>
                    </div>
                    
                    <div className="order-content">
                      <div className="customer-info">
                        <div className="customer-avatar">
                          <i className="ri-user-3-fill"></i>
                        </div>
                        <div className="customer-details">
                          <h4>{order.customer?.user?.name || 'Guest Customer'}</h4>
                          <p>{order.customer?.user?.phone_no || 'No phone'}</p>
                        </div>
                        <div className="order-amount">
                          {(order.order_items?.[0]?.currency_symbol || '')}{' '}{order.total_amount || 'N/A'}
                        </div>
                      </div>
                      
                      <div className="order-meta">
                        <div className="meta-item">
                          <i className="ri-calendar-2-line"></i>
                          {new Date(order.created_at).toLocaleDateString()}
                        </div>
                        <div className="meta-item">
                          <i className="ri-box-3-line"></i>
                          {order.order_items?.length || 0} items
                        </div>
                        <div className="meta-item">
                          <i className="ri-map-pin-line"></i>
                          {order.customer_address || 'No address'}
                        </div>
                      </div>

                      <div className="order-actions">
                        <button
                          className="btn-auto-assign"
                          disabled={assigningOrderId === order.order_id}
                          onClick={() => handleAutoAssign(order.order_id)}
                        >
                          {assigningOrderId === order.order_id ? (
                            <>
                              <div className="spinner-small"></div>
                              Assigning...
                            </>
                          ) : (
                            <>
                              <i className="ri-robot-line"></i>
                              Auto Assign
                            </>
                          )}
                        </button>
                        
                        <button
                          className="btn-manual-assign"
                          disabled={assigningOrderId === order.order_id}
                          onClick={() => openDriverSelectionModal(order.order_id)}
                        >
                          <i className="ri-user-search-line"></i>
                          Manual Assign
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

 
            </>
          )}
{pagination.current_page < pagination.last_page && (
  <div className="load-more-container">
    <button 
      className="btn-load-more"
      onClick={() => setCurrentPage(prev => prev + 1)}
    >
      Load More
    </button>
  </div>
)}

        </div>
      )}

      {/* Driver Selection Modal */}
      {modalOrderId && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-card">
              <div className="modal-header">
                <div className="header-content">
                  <div className="title-section">
                    <div className="icon-container">
                      <i className="ri-user-search-line"></i>
                    </div>
                    <div>
                      <h3 className="modal-title">Assign Delivery Drivers</h3>
                      <p className="modal-subtitle">Order #{modalOrderId}</p>
                    </div>
                  </div>
                </div>
                    {/* <button
                      key={isSendingDrivers ? 'disabled' : 'enabled'}
                      className="Driver-close-btn"
                      disabled={isSendingDrivers}
                      onClick={closeDriverSelectionModal}
                    >
                      <i className="ri-close-line"></i>
                    </button>                 */}
              </div>

              <div className="modal-body">
                {loadingNearbyDrivers[modalOrderId] ? (
                  <div className="loading-state">
                    <div className="spinner-wrapper">
                      <div className="spinner"></div>
                    </div>
                    <div className="loading-text">
                      <p>Finding nearby delivery Drivers</p>
                      <span className="loading-subtext">Searching in your area...</span>
                    </div>
                  </div>
                ) : (
                  <div className="drivers-container">
                    <div className="section-header">
                      <div className="section-title">
                        <i className="ri-group-line"></i>
                        <span>Available Drivers</span>
                        <span className="count-badge">{nearbyDriversData[modalOrderId]?.data?.drivers?.length || 0}</span>
                      </div>
                      <div className="selection-info">
                        {(selectedDriversForOrder[modalOrderId] || []).length} selected
                      </div>
                    </div>
                    
                    <div className="drivers-list">
                      {nearbyDriversData[modalOrderId]?.data?.drivers?.map(driver => {
                        const isSelected = (selectedDriversForOrder[modalOrderId] || []).includes(driver.driver_id);
                        return (
                      <div
                        key={driver.driver_id}
                        className={`driver-card ${isSelected ? 'selected' : ''} ${isSendingDrivers ? 'disabled' : ''}`}
                        onClick={() => !isSendingDrivers && 
                          handleDriverCheckboxChange(modalOrderId, driver.driver_id, !isSelected)
                        }
                      >

                            <div className="selection-indicator">
                              <div className={`checkmark ${isSelected ? 'checked' : ''}`}>
                                {isSelected && <i className="ri-check-line"></i>}
                              </div>
                            </div>
                            
                            <div className="driver-info">
                              <div className="driver-avatar">
                                <i className="ri-user-fill"></i>
                              </div>
                              <div className="driver-details">
                                <h4 className="driver-name">{driver?.name || 'Delivery Drivers'}</h4>
                                <div className="driver-meta">
                                  <span className="driver-id">
                                    <i className="ri-id-card-line"></i>
                                    ID: {driver.driver_id}
                                  </span>
                                  <span className="driver-status-online">
                                    <i className="ri-checkbox-blank-circle-fill"></i>
                                    Available
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="driver-distance">
                              <div className="distance-badge">
                                <i className="ri-map-pin-2-line"></i>
                                <span>{driver.distance_km} km</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {!nearbyDriversData[modalOrderId]?.data?.drivers?.length && (
                      <div className="empty-state">
                        <div className="empty-icon">
                          <i className="ri-user-search-line"></i>
                        </div>
                        <h4>No Drivers available</h4>
                        <p>We'll notify you when partners are nearby</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
              <button
                key={isSendingDrivers ? 'disabled-cancel' : 'enabled-cancel'}
                className="btn btn-outline-Driver"
                disabled={isSendingDrivers}
                onClick={!isSendingDrivers ? closeDriverSelectionModal : null}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                disabled={isSendingDrivers || !selectedDriversForOrder[modalOrderId]?.length}
                onClick={() => handleManualAssign(modalOrderId)}
              >
                {isSendingDrivers ? (
                  <>
                    <div className="spinner-small"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i>
                    Send to {selectedDriversForOrder[modalOrderId]?.length} Driver
                    {selectedDriversForOrder[modalOrderId]?.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>

              </div>
            </div>
          </div>
                    <button
                      key={isSendingDrivers ? 'disabled' : 'enabled'}
                      className="Driver-close-btn"
                      disabled={isSendingDrivers}
                      onClick={closeDriverSelectionModal}
                    >
                      <i className="ri-close-line"></i>
                    </button>  
        </div>
      )}
    </div>
  );
};

export default AssignOrder;